import { useEffect, useCallback, useRef, useState } from 'react';
import { getWebSocket } from '@/lib/websocket';
import { authApi } from '@/lib/auth-api';
import { addBreadcrumb, captureError } from '@/lib/telemetry';
import { useChatMessages } from './useChatMessages';
import { useChatChannels } from './useChatChannels';
import { useChatTyping } from './useChatTyping';

const WEBSOCKET_ENDPOINT = import.meta.env.VITE_WEBSOCKET_ENDPOINT || '';
const CHANNEL_REFRESH_DEBOUNCE_MS = 300;

interface UseChatReturn {
  channels: ReturnType<typeof useChatChannels>;
  messages: ReturnType<typeof useChatMessages>;
  typing: ReturnType<typeof useChatTyping>;
  subscribeToChannels: (channelIds: string[]) => void;
  unsubscribeFromChannels: (channelIds: string[]) => void;
}

export function useChat(activeChannelId: string | null): UseChatReturn {
  const channels = useChatChannels();
  const messages = useChatMessages(activeChannelId);
  const typing = useChatTyping(activeChannelId);
  const subscribedChannelsRef = useRef<Set<string>>(new Set());
  const [wsReady, setWsReady] = useState(false);
  const wsConnectingRef = useRef(false);

  // Stable refs for callbacks used in the WebSocket effect (fixes I1)
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const channelsRef = useRef(channels);
  channelsRef.current = channels;
  const activeChannelIdRef = useRef(activeChannelId);
  activeChannelIdRef.current = activeChannelId;

  // Debounced channel refresh (fixes I5)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedChannelRefresh = useCallback(() => {
    if (refreshTimerRef.current) return; // already scheduled
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      channelsRef.current.refresh();
    }, CHANNEL_REFRESH_DEBOUNCE_MS);
  }, []);

  // Initialize and connect WebSocket
  useEffect(() => {
    if (wsConnectingRef.current || wsReady) return;

    const connectWs = async () => {
      if (!WEBSOCKET_ENDPOINT) return;
      wsConnectingRef.current = true;
      try {
        const token = await authApi.getValidIdToken();
        if (!token) return;
        // Initialize the singleton if not already done
        const ws = getWebSocket(WEBSOCKET_ENDPOINT);
        if (!ws.isConnected()) {
          await ws.connect(token);
        }
        setWsReady(true);
      } catch (err) {
        captureError(err instanceof Error ? err : new Error(String(err)), {
          context: 'chat-ws-connect',
        });
      } finally {
        wsConnectingRef.current = false;
      }
    };

    connectWs();
  }, [wsReady]);

  const subscribeToChannels = useCallback((channelIds: string[]) => {
    try {
      const ws = getWebSocket();
      const newChannelIds = channelIds.filter((id) => !subscribedChannelsRef.current.has(id));
      if (newChannelIds.length > 0) {
        ws.subscribeToChatChannels(newChannelIds);
        newChannelIds.forEach((id) => subscribedChannelsRef.current.add(id));
      }
    } catch {
      addBreadcrumb('chat', 'WS not ready for subscribe', { channelIds });
    }
  }, []);

  const unsubscribeFromChannels = useCallback((channelIds: string[]) => {
    try {
      const ws = getWebSocket();
      ws.unsubscribeFromChatChannels(channelIds);
      channelIds.forEach((id) => subscribedChannelsRef.current.delete(id));
    } catch {
      addBreadcrumb('chat', 'WS not ready for unsubscribe', { channelIds });
    }
  }, []);

  // Subscribe to active (non-left) channels when they load and WebSocket is ready
  useEffect(() => {
    if (wsReady && channels.channels.length > 0) {
      const channelIds = channels.channels.filter((c) => !c.hasLeft).map((c) => c.channelId);
      subscribeToChannels(channelIds);
    }
  }, [wsReady, channels.channels, subscribeToChannels]);

  // Mark the newest message as read when messages load for the active channel
  useEffect(() => {
    if (activeChannelId && messages.messages.length > 0 && !messages.loading) {
      const newestMessage = messages.messages[messages.messages.length - 1];
      if (newestMessage) {
        messages.markAsRead(newestMessage.messageId).then(() => debouncedChannelRefresh());
      }
    }
    // Only run when the active channel changes or messages finish loading
  }, [activeChannelId, messages.loading]);

  // Wire up WebSocket events — use refs to avoid re-subscribing on every render (fixes I1)
  useEffect(() => {
    if (!wsReady) return;

    let unsubMessage: (() => void) | undefined;
    let unsubEdited: (() => void) | undefined;
    let unsubDeleted: (() => void) | undefined;
    let unsubReaction: (() => void) | undefined;
    let unsubChannelAdded: (() => void) | undefined;

    try {
      const ws = getWebSocket();

      unsubMessage = ws.onChatMessage((channelId, message) => {
        if (channelId === activeChannelIdRef.current) {
          messagesRef.current.addMessage({
            messageId: message.messageId,
            channelId,
            senderId: message.senderId,
            senderName: message.senderName,
            content: message.content,
            messageType: message.messageType as 'text' | 'system' | 'attachment' | 'reply',
            replyToId: message.replyToId,
            attachmentRefs: message.attachmentRefs ?? null,
            mentions: message.mentions,
            isEdited: false,
            editedAt: null,
            isDeleted: false,
            createdAt: message.createdAt,
          });
          // Mark as read immediately since user is viewing this channel
          messagesRef.current.markAsRead(message.messageId);
        }
        // Refresh channel list to update last message preview (debounced)
        debouncedChannelRefresh();
      });

      unsubEdited = ws.onChatMessageEdited((channelId, messageId, content, editedAt) => {
        if (channelId === activeChannelIdRef.current) {
          messagesRef.current.updateMessage(messageId, { content, isEdited: true, editedAt });
        }
      });

      unsubDeleted = ws.onChatMessageDeleted((channelId, messageId) => {
        if (channelId === activeChannelIdRef.current) {
          messagesRef.current.removeMessage(messageId);
        }
      });

      unsubReaction = ws.onChatReaction((channelId, messageId, _userId, _emoji, _action) => {
        if (channelId === activeChannelIdRef.current) {
          messagesRef.current.updateMessage(messageId, {}); // Trigger re-render; full reaction data comes from API
        }
      });

      unsubChannelAdded = ws.onChatChannelAdded((channelId) => {
        // Refresh channel list to pick up the new channel
        debouncedChannelRefresh();
        // Subscribe to the new channel for real-time messages
        subscribeToChannels([channelId]);
      });
    } catch {
      addBreadcrumb('chat', 'WS not ready for event listeners');
    }

    // On reconnect, the WS client re-sends chat_subscribe automatically.
    // Clear the local tracking set so it stays in sync.
    let unsubReconnect: (() => void) | undefined;
    try {
      const ws = getWebSocket();
      unsubReconnect = ws.onReconnect(() => {
        subscribedChannelsRef.current.clear();
        // Re-populate from the WS client's internal set (already re-subscribed)
        // Mark them so subscribeToChannels won't duplicate
        const chIds = channelsRef.current.channels.map((c) => c.channelId);
        chIds.forEach((id) => subscribedChannelsRef.current.add(id));
        // Re-fetch messages for the active channel to catch up on anything missed
        if (activeChannelIdRef.current) {
          messagesRef.current.refresh();
        }
        // Refresh channel list to pick up any new channels or unread counts
        channelsRef.current.refresh();
      });
    } catch {
      // ignored
    }

    return () => {
      unsubMessage?.();
      unsubEdited?.();
      unsubDeleted?.();
      unsubReaction?.();
      unsubChannelAdded?.();
      unsubReconnect?.();
    };
  }, [wsReady, subscribeToChannels, debouncedChannelRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const channelIds = Array.from(subscribedChannelsRef.current);
      if (channelIds.length > 0) {
        unsubscribeFromChannels(channelIds);
      }
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [unsubscribeFromChannels]);

  return {
    channels,
    messages,
    typing,
    subscribeToChannels,
    unsubscribeFromChannels,
  };
}
